import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight, Play, Film } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { getImageUrl } from "@/lib/api";
import { useMediaQuery } from "@/hooks/use-media-query";
import ShowcaseVideoModal from "@/components/home/ShowcaseVideoModal";

// Physical render window: only activeIndex-2 .. activeIndex+2 ever become real DOM
// nodes (max 5 slots total - real items or decorative ghost panels). Items outside
// this window never enter the render tree at all: no component, no <img>, no <video>,
// no network request, regardless of whether the showcase has 3 items or 200.
const WINDOW = 2;
const DRAG_THRESHOLD = 60; // px of track drag before a slide is committed

/**
 * Cylindrical placement for a card at a signed distance (`offset`) from the active
 * (center, offset 0) card. Cards sit on an actual circle - x/z come from sin/cos of the
 * slot's angle, not a flat translateX - so, combined with the shared `perspective` on
 * the ancestor, side cards genuinely recede in 3D space behind the center card instead
 * of being a flat slider with a z-index trick layered on top.
 */
function getCardTransform(offset, { reducedMotion, radius, angleStep }) {
  const abs = Math.abs(offset);
  const hidden = abs > WINDOW;
  if (reducedMotion) {
    return {
      x: offset * radius * 0.42,
      z: 0,
      rotateY: 0,
      scale: offset === 0 ? 1 : 0.88,
      opacity: hidden ? 0 : offset === 0 ? 1 : 0.55,
      zIndex: 100 - abs,
    };
  }
  const angle = offset * angleStep;
  const rad = (angle * Math.PI) / 180;
  return {
    x: Math.sin(rad) * radius,
    z: (Math.cos(rad) - 1) * radius,
    rotateY: -angle,
    scale: offset === 0 ? 1 : Math.max(0.64, 1 - abs * 0.16),
    opacity: hidden ? 0 : offset === 0 ? 1 : Math.max(0.4, 1 - abs * 0.32),
    zIndex: 100 - abs,
  };
}

/**
 * Decorative, non-interactive filler panel used only when there are fewer real
 * showcase items than physical slots (1 or 2 active items). It carries no real data,
 * plays no media and cannot be selected/focused - its sole purpose is to keep the
 * cylinder silhouette intact instead of leaving a one-sided/broken-looking gap next to
 * a lone active card.
 *
 * Visual language: a genuine frosted-glass panel (filled translucent surface + strong
 * backdrop blur + a thin elegant border + a soft top sheen/highlight), NOT a bare
 * outline - it should read as "a real card, just out of focus behind the active one",
 * never as an invisible placeholder. depthFilter/scale/opacity from getCardTransform
 * still apply (via the shared `transform` prop) so it recedes exactly like a real side
 * card and can never visually sit in front of the center card.
 */
const GhostCard = ({ offset, transform, transition, cardWidth, cardHeight }) => (
  <div
    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
    style={{ width: cardWidth, height: cardHeight, pointerEvents: "none" }}
    aria-hidden="true"
    data-testid={`showcase-coverflow-ghost-${offset}`}
  >
    <motion.div
      className="relative w-full h-full rounded-[18px] border bg-[color:hsl(var(--card))]/55 backdrop-blur-xl overflow-hidden"
      animate={transform}
      transition={transition}
      style={{
        transformPerspective: 1400,
        pointerEvents: "none",
        borderColor: "rgba(43,43,43,0.18)",
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,0.45), inset 0 0 0 1px rgba(255,255,255,0.12), 0 18px 36px rgba(43,43,43,0.18)",
      }}
    >
      {/* Soft glass sheen - a gentle top-to-bottom highlight, like light grazing a pane of glass. */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/32 via-white/5 to-black/[0.05]" />
      {/* Faint brand-gold tint so the panel still feels part of this site's palette. */}
      <div className="absolute inset-0 bg-gradient-to-br from-[color:var(--brand-gold-2)]/10 via-transparent to-transparent" />
    </motion.div>
  </div>
);

/**
 * A single real Coverflow card. Cards are always poster/image-only - no card, active or
 * not, ever mounts a real <video> element. This guarantees the homepage never issues a
 * video network request on initial load or while browsing between side cards, no matter
 * how many video items the showcase has.
 *
 * Interaction model:
 * - A non-active card's click navigates the carousel to it (unchanged from before).
 * - The ACTIVE card, if it is a video item, opens a centered modal/lightbox on click
 *   (or Enter/Space when focused) where the real <video> is mounted and played. Side
 *   cards and active image cards are otherwise inert on click, exactly as before.
 *
 * The 3D transform is applied to an INNER motion.div, kept separate from the OUTER
 * absolutely-positioned/centered wrapper and from the actual <img> element. Safari/
 * WebKit can misbehave when a single node simultaneously carries percentage centering,
 * 3D rotation/translateZ and clipping (border-radius + overflow:hidden) - splitting
 * "static centering" / "3D placement" / "clipped media" into separate nested boxes
 * avoids that class of bug.
 */
const CoverflowCard = ({ item, offset, transform, transition, cardWidth, cardHeight, onSelect, onOpenVideo }) => {
  const isActive = offset === 0;
  const isVideo = item.media_type === "video";
  const isActiveVideo = isActive && isVideo;
  const depth = Math.min(Math.abs(offset), 2);

  const handleActivate = () => {
    if (!isActive) {
      onSelect();
      return;
    }
    if (isActiveVideo) onOpenVideo(item);
  };

  const handleKeyDown = (e) => {
    if (!isActiveVideo) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpenVideo(item);
    }
  };

  // Non-active cards are deliberately dimmed/desaturated and given a softer shadow -
  // on top of the real Z-depth (translateZ) and smaller scale/opacity - so they read as
  // genuinely sitting "behind" the center card rather than merely being smaller.
  const depthFilter = isActive ? "none" : `brightness(${1 - depth * 0.13}) saturate(${1 - depth * 0.12})`;
  const shadow = isActive
    ? "shadow-[0_28px_56px_rgba(43,43,43,0.34)]"
    : "shadow-[0_12px_24px_rgba(43,43,43,0.16)]";

  return (
    <div
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
      style={{ width: cardWidth, height: cardHeight, pointerEvents: "none" }}
      data-testid={`showcase-coverflow-slot-${item.id}`}
    >
      {/*
        This outer box is a pure layout anchor (always centered, never transformed) -
        it is intentionally pointer-events:none so it can NEVER intercept hover/click
        at this shared center point. Every slot's outer anchor physically overlaps here;
        only the INNER motion.div below (which carries the actual 3D offset transform
        and is explicitly pointer-events:auto) is hit-tested, and it is only ever
        visually present at its own transformed screen position - so hover/click always
        resolves to whichever card is truly rendered under the cursor.
      */}
      <motion.div
        className={`relative w-full h-full rounded-[18px] overflow-hidden border border-[color:var(--line)] bg-[color:hsl(var(--card))] cursor-pointer ${shadow}`}
        animate={transform}
        transition={transition}
        style={{ transformPerspective: 1400, filter: depthFilter, pointerEvents: "auto" }}
        onClick={handleActivate}
        role={isActiveVideo ? "button" : undefined}
        tabIndex={isActiveVideo ? 0 : undefined}
        onKeyDown={handleKeyDown}
        aria-label={isActiveVideo ? `Videoyu oynat: ${item.title || ""}` : undefined}
        data-testid={isActive ? `showcase-coverflow-active-card-${item.id}` : `showcase-coverflow-side-card-${item.id}`}
      >
        {isVideo ? (
          item.poster_path ? (
            <img
              src={getImageUrl(item.poster_path)}
              alt={item.title}
              loading={Math.abs(offset) <= 1 ? "eager" : "lazy"}
              decoding="async"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-[color:var(--brand-charcoal)]">
              <Film className="h-8 w-8 text-white/50" />
            </div>
          )
        ) : (
          <img
            src={getImageUrl(item.media_path)}
            alt={item.title}
            loading={Math.abs(offset) <= 1 ? "eager" : "lazy"}
            decoding="async"
            className="w-full h-full object-cover"
          />
        )}

        {isActiveVideo && (
          // Static "click to play" affordance - poster stays fully visible, a dark
          // scrim + centered Play badge signal the card opens a video, matching the
          // old hover/preview visual language without any inline autoplay behavior.
          <div className="absolute inset-0 pointer-events-none" data-testid="showcase-coverflow-play-hint">
            <div className="absolute inset-0 bg-black/20" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="h-12 w-12 rounded-full bg-black/55 flex items-center justify-center">
                <Play className="h-5 w-5 text-white fill-current ml-0.5" />
              </span>
            </div>
          </div>
        )}

        {isActive && (item.title || item.description) && (
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/65 via-black/15 to-transparent">
            {item.title && (
              <h3 className="font-[var(--font-serif)] text-base text-white leading-snug" data-testid={`showcase-coverflow-title-${item.id}`}>
                {item.title}
              </h3>
            )}
            {item.description && <p className="text-xs text-white/80 mt-0.5 line-clamp-2">{item.description}</p>}
          </div>
        )}
      </motion.div>
    </div>
  );
};

const ShowcaseSection = ({ items, loading }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [videoModalItem, setVideoModalItem] = useState(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const sectionRef = useRef(null);
  const reducedMotion = useReducedMotion();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isTablet = useMediaQuery("(min-width: 640px)");

  const cardWidth = isDesktop ? 360 : isTablet ? 300 : 230;
  const cardHeight = Math.round(cardWidth * 0.75);
  const containerHeight = cardHeight + (isDesktop ? 104 : 72);
  // radius/angleStep define the circle the cards sit on - tuned so a side card's
  // projected edge clears the active card's edge (no on-top overlap) once scale,
  // rotateY foreshortening and perspective are applied.
  const radius = cardWidth * 1.25;
  const angleStep = isDesktop ? 42 : isTablet ? 40 : 36;

  // Keep the active index in range if the item count changes (e.g. admin deletes one).
  useEffect(() => {
    if (items.length > 0 && activeIndex > items.length - 1) {
      setActiveIndex(items.length - 1);
    }
  }, [items.length, activeIndex]);

  const goNext = useCallback(
    () => setActiveIndex((i) => (items.length <= 1 ? i : (i + 1) % items.length)),
    [items.length]
  );
  const goPrev = useCallback(
    () => setActiveIndex((i) => (items.length <= 1 ? i : (i - 1 + items.length) % items.length)),
    [items.length]
  );

  const handleOpenVideo = useCallback((item) => {
    setVideoModalItem(item);
    setVideoModalOpen(true);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      goNext();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      goPrev();
    }
  };

  const handleDragEnd = (_e, info) => {
    if (info.offset.x < -DRAG_THRESHOLD) goNext();
    else if (info.offset.x > DRAG_THRESHOLD) goPrev();
  };

  // Physical render window: for every slot from -WINDOW to +WINDOW we assign a real
  // item by wrapping the index circularly (modulo items.length) - this is what makes
  // navigation infinite/looping (last -> first, first -> last) with NO change to the
  // geometry/animation, only to which item each slot's transform is applied to.
  //
  // Dedup safety: when there are fewer real items than physical slots (items.length <=
  // WINDOW*2, i.e. 2-4 items), circular wrapping alone would occasionally place the
  // SAME item.id in two different slots at once. That would (a) break React's keyed
  // animation for that item (same key, two elements) and (b) visually duplicate a
  // card. Slots are therefore resolved center-outward (offset 0, then ±1, then ±2);
  // whichever slot reaches a given real item FIRST keeps it, any farther slot that
  // would repeat it renders the existing decorative ghost panel instead - exactly the
  // same ghost fallback already used when there simply aren't enough items at all.
  const slots = useMemo(() => {
    const n = items.length;
    const bySlot = new Map();
    if (n > 0) {
      const used = new Set();
      const order = [0];
      for (let d = 1; d <= WINDOW; d++) order.push(-d, d);
      for (const offset of order) {
        if (n === 1 && offset !== 0) continue; // a single item never "loops" into other slots
        const candidate = ((activeIndex + offset) % n + n) % n;
        if (used.has(candidate)) continue;
        used.add(candidate);
        bySlot.set(offset, { index: candidate, item: items[candidate] });
      }
    }
    const result = [];
    for (let offset = -WINDOW; offset <= WINDOW; offset++) {
      const slot = bySlot.get(offset);
      result.push({ offset, index: slot ? slot.index : null, item: slot ? slot.item : null });
    }
    return result;
  }, [items, activeIndex]);

  const transition = reducedMotion
    ? { duration: 0.22, ease: "easeOut" }
    : { type: "spring", stiffness: 240, damping: 28, mass: 1 };

  if (!loading && items.length === 0) return null;

  return (
    <section className="py-16 md:py-20 bg-[color:hsl(var(--secondary))] border-t border-[color:var(--line)] overflow-hidden" data-testid="home-showcase-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-xl mx-auto mb-10">
          <span className="text-sm font-medium text-[color:var(--brand-gold-2)] uppercase tracking-wide">Öne Çıkanlar</span>
          <h2 className="font-[var(--font-serif)] text-2xl md:text-3xl font-semibold tracking-[-0.01em] text-[color:var(--brand-charcoal)] mt-2">
            Vitrinimizden Seçtiklerimiz
          </h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center" style={{ height: containerHeight }}>
            <Skeleton style={{ width: cardWidth, height: cardHeight }} className="rounded-[18px]" />
          </div>
        ) : (
          <div
            ref={sectionRef}
            className="relative select-none focus:outline-none"
            style={{ height: containerHeight }}
            role="region"
            aria-roledescription="carousel"
            aria-label="Vitrin - öne çıkan içerikler"
            tabIndex={0}
            onKeyDown={handleKeyDown}
            data-testid="home-showcase-coverflow"
          >
            <div className="absolute inset-0" style={{ perspective: 1400 }}>
              <motion.div
                className="relative w-full h-full"
                style={{ touchAction: "pan-y" }}
                drag={items.length > 1 ? "x" : false}
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.15}
                onDragEnd={handleDragEnd}
              >
                {slots.map(({ offset, index, item }) => {
                  const transform = getCardTransform(offset, { reducedMotion, radius, angleStep });
                  if (!item) {
                    return (
                      <GhostCard
                        key={`ghost-${offset}`}
                        offset={offset}
                        transform={transform}
                        transition={transition}
                        cardWidth={cardWidth}
                        cardHeight={cardHeight}
                      />
                    );
                  }
                  return (
                    <CoverflowCard
                      key={item.id}
                      item={item}
                      offset={offset}
                      transform={transform}
                      transition={transition}
                      cardWidth={cardWidth}
                      cardHeight={cardHeight}
                      onSelect={() => setActiveIndex(index)}
                      onOpenVideo={handleOpenVideo}
                    />
                  );
                })}
              </motion.div>
            </div>

            {items.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Önceki"
                  onClick={goPrev}
                  data-testid="showcase-coverflow-prev-button"
                  className="absolute left-0 sm:left-2 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full bg-[color:hsl(var(--card))]/90 border border-[color:var(--line)] shadow-[0_6px_18px_rgba(43,43,43,0.18)] flex items-center justify-center text-[color:var(--brand-charcoal)] transition-opacity hover:bg-[color:hsl(var(--card))] focus-visible:ring-2 focus-visible:ring-[color:var(--brand-gold-2)]"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  aria-label="Sonraki"
                  onClick={goNext}
                  data-testid="showcase-coverflow-next-button"
                  className="absolute right-0 sm:right-2 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full bg-[color:hsl(var(--card))]/90 border border-[color:var(--line)] shadow-[0_6px_18px_rgba(43,43,43,0.18)] flex items-center justify-center text-[color:var(--brand-charcoal)] transition-opacity hover:bg-[color:hsl(var(--card))] focus-visible:ring-2 focus-visible:ring-[color:var(--brand-gold-2)]"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                <div
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 text-xs text-[color:hsl(var(--muted-foreground))] tabular-nums"
                  data-testid="showcase-coverflow-counter"
                >
                  {activeIndex + 1} / {items.length}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <ShowcaseVideoModal open={videoModalOpen} onOpenChange={setVideoModalOpen} item={videoModalItem} />
    </section>
  );
};

export default ShowcaseSection;
